import { DOCUMENT } from '@angular/common';
import { effect, inject, Injectable, signal } from '@angular/core';

export type FontSize = 'normal' | 'large' | 'largest';
export type FontFamily = 'default' | 'sans' | 'serif';
export type LetterSpacing = 'normal' | 'wide' | 'wider';
export type ColorScheme = 'default' | 'contrast' | 'grayscale' | 'blue';

export interface AccessibilitySettings {
  fontSize: FontSize;
  fontFamily: FontFamily;
  letterSpacing: LetterSpacing;
  colorScheme: ColorScheme;
  images: boolean;
  soundAssistant: boolean;
}

const STORAGE_KEY = 'salyk-kenesh:accessibility:v1';
const DEFAULT_SETTINGS: AccessibilitySettings = {
  fontSize: 'normal',
  fontFamily: 'default',
  letterSpacing: 'normal',
  colorScheme: 'default',
  images: true,
  soundAssistant: false,
};

function oneOf<T extends string>(value: unknown, options: readonly T[], fallback: T): T {
  return options.includes(value as T) ? (value as T) : fallback;
}

@Injectable({ providedIn: 'root' })
export class AccessibilityService {
  private readonly document = inject(DOCUMENT);
  readonly settings = signal<AccessibilitySettings>(this.readSettings());

  constructor() {
    effect(() => {
      const settings = this.settings();
      const root = this.document.documentElement;

      root.dataset['a11ySize'] = settings.fontSize;
      root.dataset['a11yFont'] = settings.fontFamily;
      root.dataset['a11ySpacing'] = settings.letterSpacing;
      root.dataset['a11yColor'] = settings.colorScheme;
      root.dataset['a11yImages'] = settings.images ? 'shown' : 'hidden';
      root.dataset['a11ySound'] = settings.soundAssistant ? 'on' : 'off';
    });
  }

  set<K extends keyof AccessibilitySettings>(key: K, value: AccessibilitySettings[K]): void {
    const next = { ...this.settings(), [key]: value };

    this.settings.set(next);

    try {
      this.document.defaultView?.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // The controls still work when browser storage is unavailable.
    }
  }

  reset(): void {
    this.settings.set({ ...DEFAULT_SETTINGS });

    try {
      this.document.defaultView?.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // The current page still returns to its standard appearance.
    }
  }

  private readSettings(): AccessibilitySettings {
    try {
      const raw = this.document.defaultView?.localStorage.getItem(STORAGE_KEY);

      if (!raw) return { ...DEFAULT_SETTINGS };
      const value = JSON.parse(raw) as Partial<
        Omit<AccessibilitySettings, 'colorScheme'> & { colorScheme: ColorScheme | 'inverse' }
      >;

      const settings: AccessibilitySettings = {
        fontSize: oneOf(value.fontSize, ['normal', 'large', 'largest'], DEFAULT_SETTINGS.fontSize),
        fontFamily: oneOf(value.fontFamily, ['default', 'sans', 'serif'], DEFAULT_SETTINGS.fontFamily),
        letterSpacing: oneOf(value.letterSpacing, ['normal', 'wide', 'wider'], DEFAULT_SETTINGS.letterSpacing),
        colorScheme: oneOf(
          value.colorScheme === 'inverse' ? 'contrast' : value.colorScheme,
          ['default', 'contrast', 'grayscale', 'blue'],
          DEFAULT_SETTINGS.colorScheme,
        ),
        images: typeof value.images === 'boolean' ? value.images : DEFAULT_SETTINGS.images,
        soundAssistant:
          typeof value.soundAssistant === 'boolean' ? value.soundAssistant : DEFAULT_SETTINGS.soundAssistant,
      };

      if (value.colorScheme === 'inverse') {
        try {
          this.document.defaultView?.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
        } catch {
          // The migrated settings still apply when browser storage is unavailable.
        }
      }

      return settings;
    } catch {
      return { ...DEFAULT_SETTINGS };
    }
  }
}
